/**
 * Turns the server's twelve `MonthlyIncome` rows into plot-ready series for the
 * Income & Targets chart.
 *
 * Kept out of the component, and free of React, because everything that can actually be
 * WRONG here is arithmetic: which months a line is allowed to span, what a null target
 * does to it, and where the axis tops out. Those are unit tested; the SVG is not.
 *
 * The one rule inherited from the rest of the income page: this file never invents a
 * figure the server didn't send. It only sums, slices and scales what is already there.
 */

export const CHART_MODES = {
    MONTHLY: 'MONTHLY',
    CUMULATIVE: 'CUMULATIVE',
    /**
     * One month expanded into day-of-month blocks of a week. The rows come from a
     * different endpoint and carry their own isFuture/inProgress, because a week's
     * position in time cannot be derived from a year and a month.
     */
    WEEKLY: 'WEEKLY',
};

/** Smallest axis top we will draw. Keeps a $0 year from rendering a degenerate scale. */
const MIN_AXIS_TOP = 100;

const toNumber = (value) => (value === null || value === undefined ? null : Number(value));

/** Sortable month ordinal, so "is Dec 2026 after Aug 2026" is one comparison. */
const ordinal = (year, month) => year * 12 + month;

/**
 * An axis top and the ticks under it, both landing on round numbers.
 *
 * Rounds the STEP rather than the maximum, because rounding the maximum to something
 * like 2,500 and then quartering it gives ticks of 625 — clean at the top and unreadable
 * everywhere else.
 *
 * Tries four intervals and five, and keeps whichever tops out closest above the data.
 * With four alone, $4,100 of sales snaps to a $8,000 axis and the line crawls along the
 * bottom of the card; five intervals give it a $5,000 axis in steps of $1,000.
 */
export const niceScale = (max, tickCounts = [4, 5]) => {
    const target = Math.max(Number(max) || 0, MIN_AXIS_TOP);

    const candidates = tickCounts.map((tickCount) => {
        const rough = target / tickCount;
        const exponent = Math.floor(Math.log10(rough));
        const base = 10 ** exponent;
        const fraction = rough / base;
        // 1.5 and 3 earn their place: without them a cumulative target of $513K lands on
        // an $800K axis and both lines crawl along the bottom third of the card.
        const nice = fraction <= 1 ? 1
            : fraction <= 1.5 ? 1.5
            : fraction <= 2 ? 2
            : fraction <= 2.5 ? 2.5
            : fraction <= 3 ? 3
            : fraction <= 5 ? 5
            : fraction <= 7.5 ? 7.5
            : 10;
        const step = nice * base;
        return {
            yMax: step * tickCount,
            ticks: Array.from({ length: tickCount + 1 }, (_, i) => i * step),
        };
    });

    // Ties go to the first tick count offered — fewer gridlines for the same axis top.
    return candidates.reduce((best, candidate) => (candidate.yMax < best.yMax ? candidate : best));
};

/**
 * Contiguous runs of points whose `field` is non-null.
 *
 * A target set in October and December but not November is two separate runs, not one
 * line dipping through November — drawing it as one line would assert a November target
 * of zero that nobody set.
 */
const contiguousRuns = (points, field) => {
    const runs = [];
    let run = [];
    points.forEach((point) => {
        if (point[field] === null || point[field] === undefined) {
            if (run.length) runs.push(run);
            run = [];
        } else {
            run.push(point);
        }
    });
    if (run.length) runs.push(run);
    return runs;
};

/**
 * @param months  `annualIncome.months` (twelve, in period order) or, in WEEKLY mode,
 *                `weeklyIncome.weeks` for one month
 * @param options.mode          MONTHLY, CUMULATIVE (running total) or WEEKLY
 * @param options.currentYear   today's calendar year
 * @param options.currentMonth  today's calendar month, 1-12
 */
export const buildIncomeSeries = (months, { mode = CHART_MODES.MONTHLY, currentYear, currentMonth } = {}) => {
    const rows = Array.isArray(months) ? months : [];
    const cumulative = mode === CHART_MODES.CUMULATIVE;
    const nowOrdinal = ordinal(currentYear, currentMonth);

    let salesRunning = 0;
    let awaitingRunning = 0;
    let draftRunning = 0;
    let targetRunning = 0;
    let targetStarted = false;

    const points = rows.map((row, index) => {
        // A period that hasn't happened yet has no sales — it has NO DATA. Plotting the
        // zero the server sends for it would draw ten months of collapse under a target
        // line, which is the single most misleading thing this chart could do.
        //
        // ⚠️ A WEEK's position in time cannot be derived from a year and a month, so the
        // weekly rows state it themselves and are trusted here. Months keep computing it
        // from the ordinal, which needs no extra field on a response that already works.
        const statesOwnPosition = typeof row.isFuture === 'boolean';
        const monthOrdinal = ordinal(row.year, row.month);
        const isFuture = statesOwnPosition ? row.isFuture : monthOrdinal > nowOrdinal;
        const isCurrent = statesOwnPosition ? !!row.inProgress : monthOrdinal === nowOrdinal;

        // ⚠️ PAID, not billed. The bands are money actually received, money billed and
        // still owed, and work not billed at all — three amounts that cannot overlap,
        // because an invoice has exactly one status and a payment is either in or out.
        const monthPaid = Number(row.collected) || 0;
        const monthAwaiting = Number(row.outstanding) || 0;
        const monthDraft = Number(row.draftIncGst) || 0;
        const monthSales = monthPaid;
        const monthTarget = toNumber(row.target);

        if (!isFuture) {
            salesRunning += monthPaid;
            awaitingRunning += monthAwaiting;
            draftRunning += monthDraft;
        }
        if (monthTarget !== null) {
            targetRunning += monthTarget;
            targetStarted = true;
        }

        let sales;
        let awaiting;
        let draft;
        let target;
        if (cumulative) {
            sales = isFuture ? null : salesRunning;
            awaiting = isFuture ? null : awaitingRunning;
            draft = isFuture ? null : draftRunning;
            // Once a target exists the running line continues through untargeted months as
            // a flat segment — a month with no target genuinely adds nothing to the total.
            // Before the first target there is nothing to accumulate, so no line at all.
            target = targetStarted ? targetRunning : null;
        } else {
            sales = isFuture ? null : monthPaid;
            awaiting = isFuture ? null : monthAwaiting;
            draft = isFuture ? null : monthDraft;
            target = monthTarget;
        }

        const label = row.label ?? `${row.year}-${row.month}`;
        return {
            // A week's number, so five blocks of one month get distinct keys.
            key: row.weekNumber ? `w${row.weekNumber}` : `${row.year}-${row.month}`,
            index,
            year: row.year,
            month: row.month,
            label,
            // "Sep" from "Sep 2026", but "1-7" from "1-7 Sep": the axis tick is
            // whichever part of the label distinguishes this point from its neighbours.
            monthShort: String(label).split(' ')[0],
            // Year is repeated only where it changes, so twelve ticks don't read as noise.
            // A weekly axis is one month, so it never repeats it at all.
            showYear: !row.weekNumber && (index === 0 || row.month === 1),
            sales,
            awaiting,
            draft,
            // The stack, bottom up. The TOP of it is the month if every draft were
            // billed and every invoice paid — which is the number a workshop is
            // actually chasing, and it needs no separate line to show it.
            paidTop: sales,
            awaitingTop: sales === null ? null : sales + awaiting,
            possibleTop: sales === null ? null : sales + awaiting + draft,
            target,
            monthSales,
            monthPaid,
            monthAwaiting,
            monthDraft,
            monthPossible: monthPaid + monthAwaiting + monthDraft,
            monthTarget,
            isFuture,
            isCurrent,
        };
    });

    // ⚠️ Scale on the TOP of the stack. Scaling on the paid line alone would push the
    // draft band off the top of the card in exactly the month it matters most.
    const plotted = points.flatMap((p) => [p.possibleTop, p.target])
        .filter((v) => v !== null && v !== undefined);
    const { yMax, ticks } = niceScale(plotted.length ? Math.max(...plotted) : 0);

    return {
        points,
        // Sales is one unbroken run from the start of the year to the current month; a
        // past year runs the full twelve, a future year draws nothing.
        salesSegments: contiguousRuns(points, 'sales'),
        awaitingSegments: contiguousRuns(points, 'awaitingTop'),
        possibleSegments: contiguousRuns(points, 'possibleTop'),
        targetSegments: contiguousRuns(points, 'target'),
        yMax,
        ticks,
        hasAnyTarget: points.some((p) => p.monthTarget !== null),
        hasAnySales: points.some((p) => !p.isFuture && p.monthSales > 0),
        hasAnyAwaiting: points.some((p) => !p.isFuture && p.monthAwaiting > 0),
        hasAnyDraft: points.some((p) => !p.isFuture && p.monthDraft > 0),
        cumulative,
        weekly: mode === CHART_MODES.WEEKLY,
        // True when a target is missing from at least one month the line spans, which makes
        // a cumulative target an understatement rather than the year's real goal.
        targetIsPartial: points.some((p) => p.monthTarget === null),
    };
};
